import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/app/Header";
import Avatar from "../components/app/Avatar";
import TopCampersRail from "../components/app/TopCampersRail";
import AffiliateKitBox from "../components/app/AffiliateKitBox";
import BirthdayBanner from "../components/app/BirthdayBanner";
import Footer from "../components/app/Footer";
import PollNudge from "../components/app/PollNudge";
import SignupNudgeModal from "../components/app/SignupNudgeModal";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  MessageCircle, Pin, Plus, Heart, Search, Sparkles, Loader2, X
} from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

// Distinct colour per community category — soft pastel bg + bold readable text.
// Two-class pair `bg + text + border`. Designed for AA contrast (text >= 4.5:1).
const TAG_COLORS = {
  "All":             { bg: "bg-[#f5f5f5]", text: "text-[#333]",    border: "border-[#dcdcdc]" },
  "General":         { bg: "bg-[#eef2f7]", text: "text-[#374151]", border: "border-[#cbd5e1]" },
  "Positivity":      { bg: "bg-[#fff4d6]", text: "text-[#a76b00]", border: "border-[#f5d77a]" },
  "Trip Planning":   { bg: "bg-[#e0f2fe]", text: "text-[#0369a1]", border: "border-[#7dd3fc]" },
  "Gear & Kit":      { bg: "bg-[#ede9fe]", text: "text-[#5b21b6]", border: "border-[#c4b5fd]" },
  "Safety":          { bg: "bg-[#fee2e2]", text: "text-[#b91c1c]", border: "border-[#fca5a5]" },
  "Families":        { bg: "bg-[#fce7f3]", text: "text-[#9d174d]", border: "border-[#f9a8d4]" },
  "Accessibility":   { bg: "bg-[#dbeafe]", text: "text-[#1e40af]", border: "border-[#93c5fd]" },
  "Solo Camping":    { bg: "bg-[#e0e7ff]", text: "text-[#3730a3]", border: "border-[#a5b4fc]" },
  "Pets":            { bg: "bg-[#fef3c7]", text: "text-[#92400e]", border: "border-[#fcd34d]" },
  "Tips & Tricks":   { bg: "bg-[#dcfce7]", text: "text-[#166534]", border: "border-[#86efac]" },
  "Recommendations": { bg: "bg-[#ffedd5]", text: "text-[#9a3412]", border: "border-[#fdba74]" },
  "Achievements":    { bg: "bg-[#fef9c3]", text: "text-[#854d0e]", border: "border-[#fde047]" },
};
const colorFor = (t) => TAG_COLORS[t] || TAG_COLORS["General"];

export default function Community() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const initialTag = (typeof window !== "undefined")
    ? (new URLSearchParams(window.location.search).get("tag") || "All")
    : "All";
  const [threads, setThreads] = useState([]);
  const [tags, setTags] = useState(["All"]);
  const [tag, setTag] = useState(initialTag);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = async (activeTag = tag, q = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeTag && activeTag !== "All") params.set("tag", activeTag);
      if (q.trim()) params.set("search", q.trim());
      const { data } = await api.get(`/community/threads?${params.toString()}`);
      setThreads(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/community/tags");
        setTags(["All", ...data]);
      } catch {}
    })();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(tag, search); /* eslint-disable-next-line */ }, [tag]);

  const onSearchSubmit = (e) => { e.preventDefault(); load(tag, search); };

  const pinned = useMemo(() => threads.filter((t) => t.pinned), [threads]);
  const rest = useMemo(() => threads.filter((t) => !t.pinned), [threads]);

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="community-page">
      <Header active="community" />

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <BirthdayBanner />
        {/* Guidelines banner */}
        <div className="mb-8 p-5 border hairline bg-[#fffdfa] flex items-start gap-4" data-testid="guidelines-banner">
          <Sparkles className="w-5 h-5 text-[#ff4500] mt-0.5" strokeWidth={1.5} />
          <div>
            <div className="eyebrow mb-1">{t("community.guidelinesTitle")}</div>
            <p className="text-sm text-[#333] leading-relaxed">
              {t("community.guidelinesBody")}
            </p>
          </div>
        </div>

        <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
          <div>
            <div className="eyebrow mb-3">{t("community.eyebrow")}</div>
            <h1 className="font-display text-4xl lg:text-5xl tracking-tight">{t("community.title")}</h1>
            <p className="text-[#555] mt-2 max-w-lg">{t("community.subtitle")}</p>
          </div>
          <button onClick={() => user ? setShowNew(true) : nav("/login")}
            className="btn-primary inline-flex items-center gap-2" data-testid="new-thread-btn">
            <Plus className="w-4 h-4" /> {t("community.newThread")}
          </button>
        </div>

        <form onSubmit={onSearchSubmit} className="mb-6 relative max-w-md" data-testid="community-search-form">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
          <input className="cc-input pl-10" placeholder={t("community.searchPlaceholder")}
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="community-search" />
        </form>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="tag-chips">
          {tags.map((t) => {
            const c = colorFor(t);
            const isActive = tag === t;
            return (
              <button key={t} onClick={() => setTag(t)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-all ${
                  isActive
                    ? "bg-[#111] text-white border-[#111]"
                    : `${c.bg} ${c.text} ${c.border} hover:brightness-95`
                }`}
                data-testid={`tag-chip-${t}`}>
                {t}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="p-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : threads.length === 0 ? (
          <div className="p-10 border hairline bg-white text-center">
            <p className="text-[#555] mb-4">{t("community.noThreads")}</p>
            <button onClick={() => user ? setShowNew(true) : nav("/login")} className="btn-primary">{t("community.startConversation")}</button>
          </div>
        ) : (
          <>
            {/* iter71 — recognition / retention loop */}
            <TopCampersRail windowDays={30} limit={5} />
            <div className="space-y-3">
              {/* iter194 — water-tight anon gate: pinned + first 3 threads
                  only for guests. Signed-in members see everything. */}
              {pinned.map((th, i) => <ThreadRow key={th.thread_id} t={th} i={`p${i}`} pinned />)}
              {(user ? rest : rest.slice(0, 3)).map((th, i) => <ThreadRow key={th.thread_id} t={th} i={i} />)}
            </div>
            {!user && rest.length > 3 && (
              <div
                className="mt-8 max-w-2xl mx-auto border-2 border-[#0e0e0e] bg-white p-6 sm:p-8 text-center shadow-[0_6px_0_0_#0e0e0e]"
                data-testid="community-anon-lock"
              >
                <div className="inline-flex items-center gap-1.5 text-[10px] mono uppercase tracking-widest bg-[#0e0e0e] text-white px-2 py-0.5 mb-3">
                  <MessageCircle className="w-3 h-3" /> +{rest.length - 3} more threads locked
                </div>
                <h3 className="font-display text-2xl leading-tight mb-2">
                  Sign up free to read the rest
                </h3>
                <p className="text-sm text-[#555] leading-snug mb-4">
                  Real conversations from UK campers — trip planning, gear reviews, family-friendly picks, safety, pets, more. Post your own the moment you're in.
                </p>
                <Link
                  to={`/register?next=${encodeURIComponent("/community")}`}
                  className="inline-flex items-center gap-2 bg-[#0a7f3f] text-white font-black text-xs uppercase tracking-widest px-5 py-3 shadow-[0_4px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_#111] transition-transform"
                  data-testid="community-anon-lock-signup"
                >
                  Sign up free
                </Link>
              </div>
            )}
          </>
        )}

        {/* iter67 — partner kit box on the community index */}
        <div className="mt-10">
          <AffiliateKitBox title="Kit our community trusts · partner deals" />
        </div>
      </div>

      {showNew && (
        <NewThreadModal
          tags={tags.filter((t) => t !== "All")}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(tag, search); }}
        />
      )}
      <PollNudge />
      <Footer />
    </div>
  );
}

function ThreadRow({ t: thread, i, pinned }) {
  const { t } = useTranslation();
  const cover = thread.cover_image;
  return (
    <Link to={`/community/${thread.thread_id}`}
      className="flex flex-col sm:flex-row border hairline bg-white hover:border-[#ff4500] transition-colors overflow-hidden"
      data-testid={`thread-row-${i}`}>
      {cover && (
        <div
          className="w-full h-32 sm:h-auto sm:w-40 lg:w-48 shrink-0 bg-[#fafafa] relative overflow-hidden"
          data-testid={`thread-cover-${i}`}
        >
          <img
            src={cover}
            alt=""
            loading="lazy"
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      )}
      <div className="flex-1 min-w-0 p-5 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            {pinned && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#ff4500]"><Pin className="w-3 h-3" /> {t("community.pinned")}</span>}
            {(() => {
              const c = colorFor(thread.tag);
              return (
                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border mono ${c.bg} ${c.text} ${c.border}`}>
                  {thread.tag}
                </span>
              );
            })()}
            {thread.author_badge && (
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#ffefea] text-[#ff4500]">
                {thread.author_badge}
              </span>
            )}
          </div>
          <div className="font-display text-xl leading-tight mb-1.5">{thread.title}</div>
          <p className="text-sm text-[#555] line-clamp-2">{thread.body}</p>
          <div className="flex items-center gap-2 mt-3 text-xs mono text-[#888]">
            <Avatar src={thread.author_picture} name={thread.user_name} size={22} ringed={false} testid={`thread-author-avatar-${i}`} />
            <span>{thread.user_name}</span>
            <span>·</span>
            <span>{fmt(thread.created_at)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3 text-xs mono text-[#555] shrink-0">
          <div className="flex items-center gap-1" title={t("community.replies")}><MessageCircle className="w-3.5 h-3.5" /> {thread.reply_count}</div>
          <div className="flex items-center gap-1" title={t("community.kudos")}><Heart className="w-3.5 h-3.5" /> {thread.kudos || 0}</div>
        </div>
      </div>
    </Link>
  );
}

function NewThreadModal({ tags, onClose, onCreated }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tag, setTag] = useState(tags[0] || "General");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 3) { toast.info(t("thread.toastTitleShort")); return; }
    if (body.trim().length < 5) { toast.info(t("thread.toastBodyShort")); return; }
    setBusy(true);
    try {
      await api.post("/community/threads", { title, body, tag });
      toast.success(t("thread.toastPosted"));
      onCreated();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-xl border hairline-strong" onClick={(e) => e.stopPropagation()}
        data-testid="new-thread-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b hairline">
          <div>
            <div className="eyebrow">{t("thread.modalEyebrow")}</div>
            <div className="font-display text-2xl">{t("thread.modalHeading")}</div>
          </div>
          <button onClick={onClose} data-testid="close-modal" aria-label={t("thread.closeAria")}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="eyebrow block mb-2">{t("thread.fieldTitle")}</label>
            <input className="cc-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={t("thread.titlePlaceholder")} required minLength={3} maxLength={140}
              data-testid="thread-title" />
          </div>
          <div>
            <label className="eyebrow block mb-2">{t("thread.fieldCategory")}</label>
            <div className="flex flex-wrap gap-2">
              {tags.map((tg) => {
                const c = colorFor(tg);
                return (
                  <button type="button" key={tg} onClick={() => setTag(tg)}
                    className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-all ${
                      tag === tg
                        ? "bg-[#ff4500] text-white border-[#ff4500]"
                        : `${c.bg} ${c.text} ${c.border} hover:brightness-95`
                    }`}
                    data-testid={`tag-pick-${tg}`}>{tg}</button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-2">{t("thread.fieldMessage")}</label>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <button
                type="button"
                onClick={() => {
                  if (body.trim().length > 0) {
                    if (!window.confirm("Replace your draft with the trip report template?")) return;
                  }
                  setBody(
                    "📍 Where we stayed\n— Site name + region:\n— Pitch type (tent / van / pod / etc.):\n— Nights: \n\n🌤 Weather + season\n— Conditions:\n— Time of year:\n\n👍 What worked\n— \n\n👎 What to watch\n— \n\n💡 Tips for the next camper\n— \n\n🏷 Total cost (optional)\n— "
                  );
                  setTag("Trip Reports");
                }}
                className="text-[11px] mono uppercase tracking-[0.15em] px-3 py-1 border hairline bg-[#fef9c3] text-[#7c2d12] hover:bg-[#fde68a]"
                data-testid="thread-template-trip-report"
              >
                ✎ Trip report template
              </button>
              <span className="text-[11px] text-[#888]">
                Pre-fills headings campers love to read.
              </span>
            </div>
            <textarea
              className="cc-input h-48 sm:h-56 resize-y leading-relaxed"
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 5000))}
              placeholder="Tell the story. Where you stayed, what surprised you, what you'd warn the next camper about. Tip: paragraph breaks work — long, honest trip reports earn the most replies."
              required minLength={5} maxLength={5000}
              data-testid="thread-body"
            />
            <div className="flex items-center justify-between mt-1 text-[11px] mono">
              <span className={body.trim().length > 0 && body.trim().length < 5 ? "text-amber-600" : "text-[#888]"}>
                {body.trim().length < 5
                  ? `${5 - body.trim().length} more characters to post`
                  : body.trim().length >= 200
                    ? "✓ this is the kind of detail people love"
                    : "✓ ready to post — but a few sentences more = way more replies"}
              </span>
              <span className={body.length > 4800 ? "text-amber-600 font-bold" : "text-[#888]"} data-testid="thread-body-count">
                {body.length} / 5000
              </span>
            </div>
          </div>
          <button className="btn-primary w-full" disabled={busy} data-testid="thread-submit">
            {busy ? t("thread.submitPosting") : t("thread.submitPost")}
          </button>
        </form>
      </div>
      <SignupNudgeModal />
    </div>
  );
}
